import { api } from './api';

/**
 * Authentication journey + Session Bootstrap client (docs/04-ENGINEERING/02-API-CONTRACTS.md §10.1,
 * docs/03-SECURITY/01-AUTHENTICATION.md §8.1).
 *
 * The UI thinks in JOURNEYS, not OAuth. It begins an authentication journey and then renders
 * whatever the resolver's `nextState` says — it never computes workspace resolution itself.
 */

export type AuthProvider = 'google' | 'microsoft';
export type JourneyIntent = 'sign_in' | 'create_account' | 'accept_invite';

export type NextState =
  | 'READY' | 'CHOOSER' | 'PROVISION' | 'INVITE'
  | 'SUSPENDED' | 'ACCESS_DENIED' | 'ERROR' | 'NOT_IMPLEMENTED';

export interface WorkspaceContext {
  workspaceId: string;
  workspaceName: string;
  workspaceType: 'solo' | 'chambers';
  membershipId: string;
  role: string;
  permissions: string[];
  status: string;
  subscription: { plan: string; status: string } | null;
  trial: { active: boolean; endsAt: string | null } | null;
  accessible: boolean;
  isPrimary: boolean;
  lastUsedAt: string | null;
  avatar: string | null;
  features: Record<string, unknown>;
  suspendedReason: string | null;
}

export interface SessionResponse {
  version: number;
  user: { id: string; name: string; email: string; avatar: string | null; identities: string[] };
  workspaces: WorkspaceContext[];
  lastWorkspaceId: string | null;
  defaultWorkspaceId: string | null;
  activeWorkspaceId: string | null;
  nextState: NextState;
}

const API_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');

/** The provider's LOGIN begin URL — identity-only scopes, intent stamped into the OAuth state. */
export function oauthBeginUrl(provider: AuthProvider, intent: JourneyIntent): string {
  return `${API_URL}/api/v1/auth/oauth/${provider}/begin?intent=${encodeURIComponent(intent)}`;
}

/**
 * Begin an authentication journey. A full-page redirect (not a popup) — survives provider CSP,
 * the back-button and mobile. The UI stays provider-agnostic: adding passkey/SSO later is a new
 * provider value, never a rewrite.
 */
export function beginAuthentication(opts: { provider: AuthProvider; intent: JourneyIntent }): void {
  window.location.assign(oauthBeginUrl(opts.provider, opts.intent));
}

/** Which social-login providers are actually configured on the server. */
export type AuthProviders = Record<AuthProvider, boolean>;

/**
 * Fetch the enabled social-login providers so the login page renders a "Continue with X" button
 * ONLY for providers that can actually complete — no dead buttons that bounce back "not configured".
 */
export async function fetchAuthProviders(): Promise<AuthProviders> {
  const res = await api.get<{ data: AuthProviders }>('/auth/oauth/providers');
  return res.data.data;
}

/** Call the Session Bootstrap resolver. The 401-interceptor refreshes the token from the cookie. */
export async function fetchSession(intent: JourneyIntent): Promise<SessionResponse> {
  const res = await api.get<{ data: SessionResponse }>('/auth/session', { params: { intent } });
  return res.data.data;
}

// ─── Account Settings · login methods (Identity management) ───

export interface IdentitySummary {
  id: string;
  provider: AuthProvider | 'password' | 'magic_link' | 'passkey' | 'sso';
  email: string | null;
  isPrimary: boolean;
  status: string;
  lastLoginAt: string | null;
  linkedAt: string | null;
}
export interface IdentityList {
  hasPassword: boolean;
  identities: IdentitySummary[];
}

/** The signed-in user's login methods (password + linked providers). */
export async function fetchIdentities(): Promise<IdentityList> {
  const res = await api.get<{ data: IdentityList }>('/auth/identities');
  return res.data.data;
}

/** Disconnect a provider. The backend refuses to remove the last sign-in method. */
export async function unlinkIdentity(id: string): Promise<void> {
  await api.delete(`/auth/identities/${id}`);
}

/** Set this identity as the primary login method (server enforces the single-primary invariant). */
export async function setPrimaryIdentity(id: string): Promise<void> {
  await api.post(`/auth/identities/${id}/primary`);
}

/**
 * Connect a provider to the signed-in account. The UI begins the journey; the backend (authenticated)
 * mints the provider auth URL with the user stamped into the state, then we redirect to it.
 */
export async function connectProvider(provider: AuthProvider): Promise<void> {
  const res = await api.post<{ data: { authUrl: string } }>(`/auth/oauth/${provider}/link`);
  window.location.assign(res.data.data.authUrl);
}
